import { useSyncExternalStore } from 'react';
import { putChunk, finishRecording } from '../../lib/recordings';

/**
 * What everybody on the call page knows about the recording, in one place.
 *
 * The Record button sits in the footer and the banner across the top, and
 * the signal that a recording started arrives in the page's poll handler -
 * three places that must agree, and none of them owns the others. So the
 * state lives here, outside React, and each reads it with
 * useSyncExternalStore. The uploader is here too, module-level on purpose:
 * when the person who is recording presses End, the page navigates away
 * within a tick and unmounts everything, and the last chunks still have to
 * reach the server. A queue held in a component's ref would go with it.
 *
 *   active: null, or
 *     { id, byId, byName, mine, kind, startedAt, state: 'asking' | 'recording',
 *       pending: [names still to accept], declined: name | null,
 *       asked: whether I am being asked right now }
 *   uploads: { pending, failed } - chunks not yet on the server
 *   hidden: the firm has recording off (or the server has no table yet)
 */

let state = {
  active: null,
  uploads: { pending: 0, failed: false },
  hidden: false,
  canStop: false,
  note: null
};
const listeners = new Set();

export const getRecordingState = () => state;

export function setRecordingState(patch) {
  const next = typeof patch === 'function' ? patch(state) : { ...state, ...patch };
  if (next === state) return;
  state = next;
  listeners.forEach((fn) => { try { fn(); } catch { /* a listener must not break a signal */ } });
}

const subscribe = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };

export const useRecordingState = () => useSyncExternalStore(subscribe, getRecordingState, getRecordingState);

/** Back to nothing - a new call, or a test. */
export function resetRecordingState() {
  setRecordingState({ active: null, canStop: false, note: null });
}

/* ------------------------------------------------------------ the uploader
 *
 * Chunks go up IN ORDER, one at a time, each tried three times with a pause
 * that grows. In order because the server appends them; one at a time
 * because two 5-second chunks in flight on a phone's uplink finish in either
 * order, and the retry is what carries a recording through a hiccup in the
 * wifi without losing the minute in the middle of it.
 */
const RETRIES = 3;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

class Uploader {
  constructor() {
    this.queue = [];
    this.running = false;
    this.finish = null;   // { sessionId, recordingId, durationMs } once the recorder has stopped
    this.failed = false;
  }

  publish() {
    setRecordingState((s) => ({ ...s, uploads: { pending: this.queue.length, failed: this.failed } }));
  }

  add(job) {
    this.queue.push(job);
    this.publish();
    this.run();
  }

  /** Called once the last chunk has been queued: finish follows the queue. */
  end(sessionId, recordingId, durationMs) {
    this.finish = { sessionId, recordingId, durationMs };
    this.run();
  }

  async run() {
    if (this.running) return;
    this.running = true;
    try {
      while (this.queue.length) {
        const job = this.queue[0];
        let ok = false;
        for (let attempt = 1; attempt <= RETRIES && !ok; attempt += 1) {
          try {
            await putChunk(job.sessionId, job.recordingId, job.seq, job.blob, job.mime);
            ok = true;
          } catch (err) {
            // A refusal is final - the recording was stopped or deleted on the
            // server, or the chunk is over its 8 MB - and retrying it three
            // times helps nobody. A 409 says which number the server expects
            // next: a number was skipped, so the job takes that number and
            // the retry sends it there.
            const status = err.response?.status;
            if (status === 409 && Number.isInteger(err.response?.data?.expected)) {
              job.seq = err.response.data.expected;
              continue;
            }
            if (status === 403 || status === 404 || status === 409 || status === 410 || status === 413) break;
            if (attempt < RETRIES) await wait(600 * attempt);
          }
        }
        this.queue.shift();
        if (!ok) this.failed = true;
        this.publish();
      }
      if (this.finish) {
        const f = this.finish;
        this.finish = null;
        try { await finishRecording(f.sessionId, f.recordingId, f.durationMs); } catch { /* the server times it out itself */ }
      }
    } finally {
      this.running = false;
      this.publish();
      if (this.queue.length || this.finish) this.run();
    }
  }

  get pending() { return this.queue.length; }
}

export const uploader = new Uploader();

/**
 * Leaving the page with chunks still going up loses them. Said by the
 * browser's own dialog, which is the only one that can hold a tab open.
 * Installed once, for the life of the application; it asks only while
 * something is actually pending.
 */
if (typeof window !== 'undefined' && !window.__jarvisRecordingGuard) {
  window.__jarvisRecordingGuard = true;
  window.addEventListener('beforeunload', (e) => {
    if (uploader.pending > 0 || uploader.finish) {
      e.preventDefault();
      // Chromium wants a returnValue to show its dialog; the words are its own.
      e.returnValue = 'A recording is still uploading.';
      return e.returnValue;
    }
    return undefined;
  });
}
